import niuConfig from '@/config/niuTrans';

/** 升级缓存键，避免沿用旧的空 realm / 手写 zh-en */
const AUTH_KEY = 'niuTransAuth_v2';

function normalizeBase(url = '') {
  return String(url || '').replace(/\/$/, '');
}

function normalizeLangCode(code) {
  return String(code || '').toLowerCase().replace(/_/g, '-').trim();
}

function isZhLike(codeOrName) {
  const s = normalizeLangCode(codeOrName);
  return (
    s === 'zh' ||
    s === 'zh-cn' ||
    s === 'zh-hans' ||
    s === 'chi' ||
    s === 'cn' ||
    s.startsWith('zh-') ||
    /中文|汉语|chinese/.test(String(codeOrName || ''))
  );
}

function isEnLike(codeOrName) {
  const s = normalizeLangCode(codeOrName);
  return (
    s === 'en' ||
    s === 'en-us' ||
    s === 'en-gb' ||
    s === 'eng' ||
    s.startsWith('en-') ||
    /英文|英语|english/.test(String(codeOrName || ''))
  );
}

function langMatches(actual, prefer) {
  const a = normalizeLangCode(actual);
  const p = normalizeLangCode(prefer);
  if (!a) return false;
  if (!p) return true;
  if (a === p) return true;
  if (isZhLike(p) && isZhLike(a)) return true;
  if (isEnLike(p) && isEnLike(a)) return true;
  return false;
}

function normalizeLibId(id) {
  if (id == null || id === '' || id === '0' || id === 0) return '';
  return String(id);
}

export function getStoredAuth() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore */ }
  return null;
}

export function setStoredAuth(data) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(data || {}));
}

export function clearStoredAuth() {
  localStorage.removeItem(AUTH_KEY);
  try { localStorage.removeItem('niuTransAuth'); } catch (e) { /* ignore */ }
}

/**
 * 登录（对齐插件 LOGIN_ACTION）
 */
async function loginByPassword() {
  if (!niuConfig.username || !niuConfig.password) {
    throw new Error('请先在 src/config/niuTrans.js 配置 username、password');
  }
  const loginUrl = `${normalizeBase(niuConfig.ipAddress)}/niuInterface/logon?pluginType=webPlugin`;
  const body = new URLSearchParams({
    username: niuConfig.username,
    password: niuConfig.password,
  });
  const res = await fetch(loginUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded;charset=utf-8' },
    body,
  });
  const json = await res.json();
  if (!json || json.code !== 200 || !json.data) {
    throw new Error((json && json.msg) || '小牛翻译登录失败');
  }
  return {
    ipAddress: normalizeBase(niuConfig.ipAddress),
    token: json.data.token,
    apikey: json.data.apikey,
    userId: json.data.userId,
    termId: normalizeLibId(niuConfig.termId),
    memoryId: normalizeLibId(niuConfig.memoryId),
  };
}

/**
 * 确保 token/apikey（对齐插件登录态）
 */
export async function ensureAuth() {
  const stored = getStoredAuth() || {};
  let auth = {
    ipAddress: normalizeBase(stored.ipAddress || niuConfig.ipAddress),
    token: stored.token || niuConfig.token || '',
    apikey: stored.apikey || niuConfig.apikey || '',
    userId: stored.userId,
    termId: normalizeLibId(stored.termId || niuConfig.termId),
    memoryId: normalizeLibId(stored.memoryId || niuConfig.memoryId),
    lanList: stored.lanList || null,
    area: stored.area || niuConfig.area || '',
    src: stored.src || '',
    tgt: stored.tgt || '',
  };

  if (auth.token && auth.apikey) {
    return auth;
  }

  if (niuConfig.token && niuConfig.apikey) {
    auth.token = niuConfig.token;
    auth.apikey = niuConfig.apikey;
    auth.ipAddress = normalizeBase(niuConfig.ipAddress);
    setStoredAuth(auth);
    return auth;
  }

  const logged = await loginByPassword();
  auth = { ...auth, ...logged, lanList: null, src: '', tgt: '', area: niuConfig.area || '' };
  setStoredAuth(auth);
  return auth;
}

/**
 * 获取领域与语种（对齐插件 GET_LANLIST / getRealmAndLanguage）
 */
export async function fetchRealmAndLanguage(auth) {
  const base = normalizeBase(auth.ipAddress);
  const url = `${base}/niuInterface/getRealmAndLanguage?apikey=${encodeURIComponent(auth.apikey)}&pluginType=webPlugin`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'content-type': 'application/json;charset=utf-8',
      'X-Access-Token': auth.token,
    },
  });
  const json = await res.json();
  if (!json || json.code !== 200) {
    if (json && (json.code === 401 || json.code === 403)) clearStoredAuth();
    throw new Error((json && json.msg) || '获取领域与语种失败');
  }
  const data = json.data;
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.records)) return data.records;
  if (data && Array.isArray(data.list)) return data.list;
  return [];
}

/**
 * 按插件逻辑挑选领域 + 翻译方向
 */
export function pickDirection(lanList, options = {}) {
  if (!Array.isArray(lanList) || !lanList.length) {
    throw new Error('语种列表为空，请检查小牛后台是否配置了领域/语种');
  }

  const preferArea = options.area != null && options.area !== '' ? String(options.area) : '';
  const preferSrc = options.sourceLanguage || niuConfig.sourceLanguage || 'zh';
  const preferTgt = options.targetLanguage || niuConfig.targetLanguage || 'en';

  const realms = [];
  if (preferArea) {
    const hit = lanList.find((item) =>
      String(item.realmId) === preferArea ||
      String(item.realmCode) === preferArea
    );
    if (hit) realms.push(hit);
  }
  lanList.forEach((item) => {
    if (!realms.includes(item)) realms.push(item);
  });

  function findPair(langs) {
    return (
      langs.find((item) => langMatches(item.from, preferSrc) && langMatches(item.to, preferTgt)) ||
      langs.find((item) =>
        (langMatches(item.from, preferSrc) || isZhLike(item.srcLanguageName)) &&
        (langMatches(item.to, preferTgt) || isEnLike(item.tgtLanguageName))
      ) ||
      langs.find((item) =>
        (isZhLike(item.from) || isZhLike(item.srcLanguageName)) &&
        (isEnLike(item.to) || isEnLike(item.tgtLanguageName))
      ) ||
      null
    );
  }

  let realm = null;
  let pair = null;
  for (let i = 0; i < realms.length; i++) {
    const langs = realms[i].langsList || realms[i].languageList || realms[i].languages || [];
    if (!Array.isArray(langs) || !langs.length) continue;
    const found = findPair(langs);
    if (found) {
      realm = realms[i];
      pair = found;
      break;
    }
  }

  if (!realm || !pair) {
    const sampleRealm = lanList[0];
    const sampleLangs = (sampleRealm && (sampleRealm.langsList || sampleRealm.languageList || [])) || [];
    const sample = sampleLangs.slice(0, 5).map((item) => `${item.from}->${item.to}`).join(', ');
    throw new Error(`未找到中译英方向，可用方向示例：${sample || '无'}（请在小牛后台检查领域语种）`);
  }

  return {
    area: realm.realmId != null ? String(realm.realmId) : String(realm.realmCode || ''),
    src: pair.from,
    tgt: pair.to,
    realmName: realm.realmName || '',
    srcName: pair.srcLanguageName || '',
    tgtName: pair.tgtLanguageName || '',
  };
}

/**
 * 登录 + 拉语种 + 选方向（对齐插件点翻译前的完整准备）
 */
export async function ensureTranslateContext(options = {}) {
  let auth = await ensureAuth();
  let lanList = auth.lanList;

  const needFetch = !Array.isArray(lanList) || !lanList.length;
  if (needFetch) {
    try {
      lanList = await fetchRealmAndLanguage(auth);
    } catch (err) {
      // token 失效则重新登录再拉一次
      clearStoredAuth();
      auth = await ensureAuth();
      lanList = await fetchRealmAndLanguage(auth);
    }
  }

  const direction = pickDirection(lanList, {
    area: options.area != null ? options.area : (niuConfig.area || auth.area || ''),
    sourceLanguage: options.src || niuConfig.sourceLanguage || 'zh',
    targetLanguage: options.tgt || niuConfig.targetLanguage || 'en',
  });

  auth = {
    ...auth,
    lanList,
    area: direction.area,
    src: direction.src,
    tgt: direction.tgt,
    realmName: direction.realmName,
    termId: normalizeLibId(niuConfig.termId || auth.termId),
    memoryId: normalizeLibId(niuConfig.memoryId || auth.memoryId),
  };
  setStoredAuth(auth);
  return auth;
}

/**
 * 批量翻译（对齐插件 list_translate / batchTrans）
 * @param {string[]} texts
 * @param {{ src?: string, tgt?: string, area?: string }} options
 * @returns {Promise<string[]>}
 */
export async function batchTranslate(texts, options = {}) {
  if (!texts || !texts.length) return [];
  // 可传入已解析的 auth，避免每一批都重复登录/拉语种
  const auth = options.auth || (await ensureTranslateContext(options));

  const src = auth.src;
  const tgt = auth.tgt;
  const area = auth.area;
  const termId = auth.termId || '';
  const memoryId = auth.memoryId || '';

  if (!src || !tgt || !area) {
    throw new Error('翻译方向不完整：请确认小牛后台已配置领域与中英语种');
  }

  const base = normalizeBase(auth.ipAddress);
  const url = `${base}/niuInterface/batchTrans?pluginType=webPlugin&apikey=${encodeURIComponent(auth.apikey)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json;charset=utf-8',
      'X-Access-Token': auth.token,
    },
    body: JSON.stringify({
      termDictionaryLibraryId: termId,
      translationMemoryLibraryId: memoryId,
      sourceLanguageAbbreviation: src,
      targetLanguageAbbreviation: tgt,
      batchStr: texts,
      realmCode: area,
    }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!json || json.code !== 200) {
    if (json && (json.code === 401 || json.code === 403)) {
      clearStoredAuth();
    }
    throw new Error((json && json.msg) || '批量翻译失败');
  }

  const data = json.data;
  if (!Array.isArray(data)) return texts;

  return data.map((item, index) => {
    if (item == null) return texts[index];
    if (typeof item === 'string') return item;
    if (typeof item.tgt_text === 'string') return item.tgt_text;
    if (typeof item.target === 'string') return item.target;
    if (typeof item.translation === 'string') return item.translation;
    return texts[index];
  });
}